import React from 'react';
import { Grid, MenuItem, TextField, Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { MultiSelect } from '@campfire/multi-select';
import { CreatWidgetFormValues, DimensionEnum } from '../dashboard-widget-model';
import { WidgetContext } from '../WidgetContext';
import { FieldValueSource, getFieldOptions, isFieldValueTarget } from '../useFieldValueFields';
import { useCampfireTheme } from '../../../../../../../theme/useCampfireTheme';
import { useWidgetContextForm } from '../useWidgetContextForm';
import { useApplyCustomWords } from '../../useCustomWords';

const FIELD_LABELS: { [source in FieldValueSource]: string } = {
  reporting: 'Report field',
  tasks: 'Task field',
  forms: 'Form field',
};

export const useFieldValueSource = (): FieldValueSource | null => {
  const { values } = useFormikContext<CreatWidgetFormValues>();
  const target = values.byValues.find(({ tag, name }) => isFieldValueTarget(tag, name));

  return target ? (target.tag as FieldValueSource) : null;
};

export const useSelectedFieldOptions = () => {
  const source = useFieldValueSource();
  const { values } = useFormikContext<CreatWidgetFormValues>();
  const { fieldValueFields } = React.useContext(WidgetContext);
  const field = source ? fieldValueFields[source].find(({ fieldId }) => fieldId === values.fieldIds[0]) : undefined;

  return getFieldOptions(source, field);
};

export const useFieldValueScope = () => {
  const source = useFieldValueSource();

  return {
    showPrograms: source !== 'forms',
    showActivities: source === null || source === 'reporting',
  };
};

/**
 * Chart entity dimensions for a field value. Only report answers belong to an
 * activity, so only they can be split by program, activity or tag.
 */
export const fieldValueDimensions = (source: FieldValueSource, optionLike: boolean) =>
  (source === 'reporting'
    ? [DimensionEnum.BY_PROGRAM, DimensionEnum.BY_ACTIVITY, DimensionEnum.BY_ACTIVITY_TAG]
    : []
  ).concat(optionLike ? [DimensionEnum.BY_FIELD_OPTION] : []);

export const FieldValueFilters = ({
  labelClassName,
  withPrograms = false,
}: {
  labelClassName: string;
  withPrograms?: boolean;
}) => {
  const cw = useApplyCustomWords();
  const { theme } = useCampfireTheme();
  const { programs } = useWidgetContextForm();
  const source = useFieldValueSource();
  const { values, setFieldValue } = useFormikContext<CreatWidgetFormValues>();
  const { fieldValueFields } = React.useContext(WidgetContext);
  const options = useSelectedFieldOptions();

  if (!source) {
    return null;
  }

  const fields = fieldValueFields[source];
  const fieldId = values.fieldIds[0] ?? '';

  return (
    <>
      {withPrograms && source === 'tasks' && (
        <Grid item xs={6}>
          <Typography className={labelClassName}>{cw('Programs')}</Typography>
          <MultiSelect
            variant='outlined'
            name='programIds'
            label={cw('Select program/s')}
            placeholder={cw('All Programs')}
            items={programs}
            style={{ borderRadius: '8px' }}
          />
        </Grid>
      )}
      <Grid item xs={6}>
        <Typography className={labelClassName}>{FIELD_LABELS[source]}</Typography>
        <TextField
          fullWidth
          select
          variant='outlined'
          label='Select field'
          value={fields.some((field) => field.fieldId === fieldId) ? fieldId : ''}
          InputProps={{
            style: { borderRadius: '8px', color: theme.color.grey.neutral300 },
          }}
          onChange={(e) => {
            setFieldValue('fieldIds', [e.target.value]);
            setFieldValue('optionIds', []);
          }}
        >
          {fields.map((field) => (
            <MenuItem key={field.fieldId} value={field.fieldId}>
              {field.groupName ? `${field.groupName} > ${field.name}` : field.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {options.length > 0 && (
        <Grid item xs={6}>
          <Typography className={labelClassName}>Options</Typography>
          <MultiSelect
            variant='outlined'
            name='optionIds'
            placeholder='All options'
            label='Select option/s'
            items={options.map((option) => ({ value: option.id, label: option.name }))}
            style={{ borderRadius: '8px' }}
          />
        </Grid>
      )}
    </>
  );
};
